import { Spin } from "antd";
import { type FC, useEffect, useMemo, useState } from "react";
import { useCallback } from "react";

import type { ContractAppProfileEntity } from "src/app/features/Contract/types/subTypes/ContractAppProfileEntity";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import type { ExtendedColumnsType } from "../../../index";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import { displayEnumValue } from "../../../Table/components/PropertyTable/transformations/cellTransformations";
import type { AntCustomFormField, CommonFieldAttributes } from "../../types/types";
import { useLazyLoadAcceptedCardProfilesVirtualQuery } from "./ContractsAcceptedCardTypesServerApi";
import type { LoadContractsAppProfilesRequest } from "./types";

type ComponentState = {
    siteId?: number;
    centralized?: boolean;
    shouldUpdateData: boolean;
    queryConfig?: LoadContractsAppProfilesRequest;
};

export interface VirtualContractsAppProfilesTableProps extends AntCustomFormField<ContractAppProfileEntity[]>, CommonFieldAttributes {
    siteId?: number;
    centralized?: boolean;
    id: number;
}

export const ContractsVirtualAcceptedCardTypesTable: FC<VirtualContractsAppProfilesTableProps> = ({
    value = [],
    onChange,
    siteId,
    centralized,
    id,
    readOnly,
}) => {
    const getColumnsConfig = useCallback((t: Function): ExtendedColumnsType<ContractAppProfileEntity>[] => {
        return [
            {
                title: t("genericTable.column.id"),
                key: "cardTypeId",
                dataIndex: "cardTypeId",
            },
            {
                title: t("applicationProfileCreateForm.table.cardType"),
                key: "cardTypeName",
                dataIndex: "cardTypeName",
            },
            {
                title: t("contractCreateForm.acceptMode"),
                key: "acceptMode",
                dataIndex: "acceptMode",
                render: (cellValue) => displayEnumValue(cellValue, "accept-mode"),
            },
        ];
    }, []);

    const dataGroupCode = useMemo(() => getAssignedDataGroup().code, []);

    // Handle data changes
    const onDataChanged = useCallback(
        (tableValue: ContractAppProfileEntity[]) => {
            if (onChange) {
                onChange(tableValue);
            }
        },
        [onChange],
    );

    const [state, setState] = useState<ComponentState>({
        shouldUpdateData: false,
        centralized,
        siteId,
    });

    const [execQuery, { isFetching }] = useLazyLoadAcceptedCardProfilesVirtualQuery();

    // Build query config based on prop changes and state
    const buildQueryConfig = useCallback(() => {
        if (state.siteId === undefined) {
            return {
                siteId,
                centralized,
                shouldUpdateData: false,
            };
        }

        const centralizedHasChanged = state.centralized !== centralized && state.centralized !== undefined;
        const siteIdHasChanged = state.siteId !== siteId && state.siteId !== undefined;
        const shouldUpdateData = !!siteId && (centralizedHasChanged || siteIdHasChanged);

        if (shouldUpdateData) {
            const siteParams = `${id}/`;
            const siteIdString = `?siteId=${siteId}`;
            const centralizedString = `&centralized=${centralized ?? false}`;

            const queryParams = `${siteIdString}${centralizedString}`;

            const queryConfig = { siteParams, dataGroupCode, queryParams };

            return {
                siteId,
                centralized,
                queryConfig,
                shouldUpdateData,
            };
        }
        return null;
    }, [state, centralized, siteId, id, dataGroupCode]);

    // Watch for prop changes and update state
    useEffect(() => {
        const result = buildQueryConfig();
        if (result) {
            setState(result);
        }
    }, [buildQueryConfig]);

    useEffect(() => {
        if (state.shouldUpdateData && state.queryConfig) {
            execQuery(state.queryConfig)
                .unwrap()
                .then((data) => {
                    onDataChanged(data.items);
                })
                .catch(() => {});

            setState({ ...state, shouldUpdateData: false });
        }
    }, [state, setState, execQuery, onDataChanged]);

    return (
        <Spin spinning={isFetching}>
            <PropertyTable<ContractAppProfileEntity>
                name="contractsVirtualAcceptedCardTypes_table"
                rowsAreSelectable={false}
                getColumnsConfig={getColumnsConfig}
                propertyAsRowKey="cardTypeId"
                tableData={value}
                readOnly={readOnly || false}
            />
        </Spin>
    );
};
